/* Hero hotspots (§10.1). Auto-tour: the active index row's progress bar runs for --tour, and its
   animationend moves to the next hotspot. Hovering or focusing a dot / row activates it and holds the
   tour; it resumes 6s after the pointer or focus leaves. The pause button stops it for good (WCAG 2.2.2).
   On phones the photo pans so the active hotspot stays in view. No tour with reduced motion. */
class HeroRoom extends HTMLElement {
  connectedCallback() {
    this.spots = [...this.querySelectorAll('[data-spot]')];
    this.rows = [...this.querySelectorAll('[data-index-row]')];
    if (!this.spots.length) return;
    this.box = this.querySelector('[data-hero-box]');
    this.toggle = this.querySelector('[data-tour-toggle]');
    this.phone = matchMedia('(max-width: 639px)');
    this.autoplay = this.dataset.autoplay === 'true' && this.spots.length > 1 && window.Theme.motionAllowed();
    this.userPaused = false;
    this.held = false;

    // Triggers carry the block index; hotspots without a product are skipped, so map to a position.
    this.onEnter = (event) => {
      const trigger = event.target.closest('[data-spot-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      const position = this.spots.findIndex((spot) => spot.dataset.spot === trigger.dataset.spotTrigger);
      if (position === -1) return;
      this.activate(position);
      this.hold();
    };
    this.onLeave = (event) => {
      if (event.type === 'focusout' && this.contains(event.relatedTarget)) return;
      this.release();
    };
    this.onBarEnd = (event) => {
      if (event.animationName === 'hero-bar' && this.isPlaying()) this.activate(this.index + 1);
    };
    this.onToggle = () => {
      this.userPaused = !this.userPaused;
      this.sync();
    };
    this.onBlockSelect = (event) => {
      const index = this.spots.findIndex((spot) => spot === event.target || spot.contains(event.target));
      if (index === -1) return;
      this.activate(index);
      this.userPaused = true;
      this.sync();
    };
    this.onResize = () => this.pan();

    this.addEventListener('pointerover', this.onEnter);
    this.addEventListener('focusin', this.onEnter);
    this.addEventListener('pointerleave', this.onLeave);
    this.addEventListener('focusout', this.onLeave);
    this.addEventListener('animationend', this.onBarEnd);
    if (this.toggle) this.toggle.addEventListener('click', this.onToggle);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.classList.toggle('is-tour', this.autoplay);
    this.activate(0);
    this.sync();
  }

  disconnectedCallback() {
    clearTimeout(this.resumeTimer);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    window.removeEventListener('resize', this.onResize);
  }

  isPlaying() {
    return this.autoplay && !this.userPaused && !this.held;
  }

  sync() {
    this.classList.toggle('is-playing', this.isPlaying());
    this.classList.toggle('is-held', this.autoplay && !this.isPlaying());
    if (this.toggle) this.toggle.setAttribute('aria-pressed', String(this.userPaused));
  }

  hold() {
    clearTimeout(this.resumeTimer);
    this.held = true;
    this.sync();
  }

  release() {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.held = false;
      this.sync();
    }, 6000);
  }

  activate(index) {
    const count = this.spots.length;
    const next = (index + count) % count;
    if (next === this.index) return;
    this.index = next;
    this.spots.forEach((spot, i) => spot.classList.toggle('is-active', i === next));
    this.rows.forEach((row, i) => row.classList.toggle('is-active', i === next));
    const spot = this.spots[next];
    this.style.setProperty('--hx', spot.style.getPropertyValue('--x'));
    this.style.setProperty('--hy', spot.style.getPropertyValue('--y'));
    this.pan();
    this.revealRow(this.rows[next]);
  }

  // Keep the active chip visible in the scrolling row (tablet / phone) without moving the page.
  revealRow(row) {
    const list = row && row.parentElement;
    if (!list || list.scrollWidth <= list.clientWidth) return;
    list.scrollTo({ left: row.offsetLeft - 20, behavior: window.Theme.motionAllowed() ? 'smooth' : 'auto' });
  }

  // Phones: shift the photo so the active dot is centred, clamped so the photo's edges never show.
  pan() {
    if (!this.box) return;
    if (!this.phone.matches) {
      this.box.style.removeProperty('--panx');
      return;
    }
    const frame = this.box.parentElement.clientWidth;
    const width = this.box.offsetWidth;
    const x = (parseFloat(this.spots[this.index].style.getPropertyValue('--x')) / 100) * width;
    const offset = Math.min(0, Math.max(frame - width, frame / 2 - x));
    this.box.style.setProperty('--panx', `${offset.toFixed(1)}px`);
  }
}

if (!customElements.get('hero-room')) customElements.define('hero-room', HeroRoom);
